import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Edit, Trash2, RotateCcw, ChevronUp } from "react-feather";
import IndependentSKU from "./independentSKU";
import { deleteMarketingSKU, getMarketingSKUs } from "../../core/api/marketing";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { secureExportToExcel } from "../../utils/secureExcelUtils";
import Table from "../../core/pagination/datatable";
import { useSelector, useDispatch } from "react-redux";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { setToogleHeader } from "../../core/redux/action";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { message } from "antd";
import { getProfile } from "../../core/api/auth.js";
import OfferCodesPopover from "./components/OfferCodesModal";

const OfferPricingList = () => {
  const [isRightSideBarVisible, setIsRightSideBarVisible] = useState(false);
  const [offerPricings, setOfferPricings] = useState([]);
  const MySwal = withReactContent(Swal);
  const [currentMarketingSKUId, setCurrentMarketingSKUId] = useState(null);
  const sidebarRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [apiTiming, setApiTiming] = useState({});
  const selectedClients = useSelector((state) => state.selectedClients);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [clientId, setClientId] = useState(null);

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  // Using getMarketingSKUs from the API file

  const fetchOfferPricings = async () => {
    setLoading(true);

    try {
      // No pagination parameters needed
      const response = await getMarketingSKUs(selectedClients);

      const { results, total, timing } = response.data;

      if (!results || !Array.isArray(results)) {
        setOfferPricings([]);
        setFilteredOfferPricings([]);
        return;
      }

      setTotalItems(total);
      setApiTiming(timing || {});

      // Data is already formatted on the backend
      setOfferPricings(results);
      setFilteredOfferPricings(results);

      // Apply search filter if one exists
      if (searchQuery) {
        setFilteredOfferPricings(
          results.filter(
            (item) => item.sku && item.sku.toLowerCase().includes(searchQuery.toLowerCase())
          )
        );
      }
    } catch (err) {
      console.error("Error fetching marketing SKUs:", err);
      setOfferPricings([]);
      setFilteredOfferPricings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOfferPricings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClients]);

  // No pagination or scroll handling needed
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOfferPricings, setFilteredOfferPricings] = useState([]);

  const handleDeleteMarketingSKU = async (offerPricingId) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this Marketing SKU? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await deleteMarketingSKU(offerPricingId);
        fetchOfferPricings();
        MySwal.fire("Deleted!", "The Marketing SKU has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting Marketing SKU:", error);
        MySwal.fire("Error!", "Failed to delete Marketing SKU. Please try again.", "error");
      }
    }
  };

  const processedData = filteredOfferPricings.flatMap((pricing) =>
    pricing.marketing_dependent_skus_details.map((item, index) => ({
      key: `${pricing.id}-${index}`,
      sku: index === 0 ? pricing.sku : "",
      offer_codes: index === 0 ? pricing.offer_codes : [], // Add offer codes
      client_id: pricing.client_id,
      dependent_sku: item.sku,
      variation_code: item.variation_code,
      unit_description: item.unit_description,
      rule: item.rule,
      quantity: item.quantity,
      quantity_detailed: item.quantity_detailed,
      product_type: item.product_type,
      description: item.description,
      original_price: item.original_price,
      discount: item.discount,
      final_price: item.final_price,
      optional: item.optional,
      bundle_rule: index === 0 ? pricing.bundle_rule : "",
      bundle_price: index === 0 ? pricing.bundle_price : "",
      bundle_discount: index === 0 ? pricing.bundle_discount : "",
      bundle_price_to_pay: index === 0 ? pricing.bundle_price_to_pay : "",
      rowSpan: index === 0 ? pricing.marketing_dependent_skus_details.length : 0,
      id: pricing.id,
    }))
  );

  useEffect(() => {
    const updateSidebarWidth = () => {
      if (sidebarRef.current) {
        setSidebarWidth(sidebarRef.current.getBoundingClientRect().width);
      }
    };

    if (isRightSideBarVisible) {
      updateSidebarWidth();
      window.addEventListener("resize", updateSidebarWidth);
    }

    return () => {
      window.removeEventListener("resize", updateSidebarWidth);
    };
  }, [isRightSideBarVisible]);

  const columns = [
    {
      title: "Marketing SKU",
      dataIndex: "sku",
      key: "sku",
      render: (text, record) => ({
        children: text,
        props: { rowSpan: record.rowSpan },
      }),
      width: 95,
    },
    {
      title: "Code Offer",
      dataIndex: "offer_codes",
      key: "offer_codes",
      render: (offerCodes, record) => {
        if (!offerCodes || offerCodes.length === 0) {
          return {
            children: <span style={{ color: "#999" }}>No Offers</span>,
            props: { rowSpan: record.rowSpan },
          };
        }

        if (offerCodes.length <= 1) {
          return {
            children: (
              <div>
                {offerCodes.map((code, index) => (
                  <div key={index} style={{ marginBottom: "2px" }}>
                    <span className="badge badge-info">{code}</span>
                  </div>
                ))}
              </div>
            ),
            props: { rowSpan: record.rowSpan },
          };
        } else {
          return {
            children: (
              <div>
                <div style={{ marginBottom: "2px" }}>
                  <span className="badge badge-info">{offerCodes[0]}</span>
                </div>
                <div>
                  <OfferCodesPopover
                    offerCodes={offerCodes}
                    title={`Offer Codes for SKU: ${record.sku}`}>
                    <Link
                      to="#"
                      style={{
                        color: "#1890ff",
                        fontSize: "12px",
                        textDecoration: "underline",
                      }}>
                      +{offerCodes.length - 1} more
                    </Link>
                  </OfferCodesPopover>
                </div>
              </div>
            ),
            props: { rowSpan: record.rowSpan },
          };
        }
      },
      width: 120,
    },
    {
      title: "Marketing Dependent SKU",
      dataIndex: "dependent_sku",
      key: "dependent_sku",
      width: 100,
    },
    {
      title: "Item Variation SKU",
      dataIndex: "variation_code",
      key: "variation_code",
      width: 100,
    },
    {
      title: "Category",
      dataIndex: "product_type",
      key: "product_type",
      width: 100,
    },
    {
      title: "Unit Description",
      dataIndex: "unit_description",
      key: "unit_description",
      width: 180,
    },

    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 60,
    },
    {
      title: "Qty Detailed",
      dataIndex: "quantity_detailed",
      key: "quantity_detailed",
      width: 100,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Price",
      dataIndex: "original_price",
      key: "original_price",
      render: (price) => `$${price || 0}`,
      width: 90,
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      render: (price) => `$${price || 0}`,
      width: 100,
    },
    {
      title: "Price to Pay",
      dataIndex: "final_price",
      key: "final_price",
      render: (price) => `$${price || 0}`,
      width: 80,
    },
    {
      title: "Rule",
      dataIndex: "rule",
      key: "rule",
      width: 100,
    },
    {
      title: "Bundle Price",
      dataIndex: "bundle_price",
      key: "bundle_price",
      render: (text, record) => ({
        children: text ? `$${text}` : "",
        props: { rowSpan: record.rowSpan },
      }),
      width: 90,
    },
    {
      title: "Bundle Discount",
      dataIndex: "bundle_discount",
      key: "bundle_discount",
      render: (text, record) => ({
        children: text ? `$${text}` : "",
        props: { rowSpan: record.rowSpan },
      }),
      width: 90,
    },
    {
      title: "Bundle Price to Pay",
      dataIndex: "bundle_price_to_pay",
      key: "bundle_price_to_pay",
      render: (text, record) => ({
        children: text ? `$${text}` : "",
        props: { rowSpan: record.rowSpan },
      }),
      width: 90,
    },
    {
      title: "Bundle Rule",
      dataIndex: "bundle_rule",
      key: "bundle_rule",
      render: (text, record) => ({
        children: text,
        props: { rowSpan: record.rowSpan },
      }),
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (text, record) => ({
        children: (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                onClick={() => {
                  setCurrentMarketingSKUId(record.id);
                  setClientId(record.client_id);
                  setIsRightSideBarVisible(true);
                }}>
                <Edit className="feather-edit" />
              </Link>
              <Link className="p-2" to="#" onClick={() => handleDeleteMarketingSKU(record.id)}>
                <Trash2 className="feather-trash-2" />
              </Link>
            </div>
          </div>
        ),
        props: { rowSpan: record.rowSpan },
      }),
    },
  ];

  const exportToExcel = () => {
    if (!offerPricings.length) {
      message.info("No data available for export.");
      return;
    }

    const exportData = offerPricings.flatMap((pricing) =>
      pricing.marketing_dependent_skus_details.map((item, index) => ({
        "Marketing SKU": index === 0 ? pricing.sku : "",
        "Code Offer":
          index === 0 && pricing.offer_codes && pricing.offer_codes.length > 0
            ? pricing.offer_codes.join(", ")
            : index === 0
            ? "-"
            : "",
        "Dependent SKU": item.sku,
        Category: item.product_type,
        Description: item.description,
        Quantity: item.quantity,
        "Quantity Detailed": item.quantity_detailed,
        "Original Price": `$${item.original_price || 0}`,
        Discount: `$${item.discount || 0}`,
        "Final Price": `$${item.final_price || 0}`,
        Optional: item.optional || "",
        "Bundle Price": index === 0 ? `$${pricing.bundle_price}` : "",
        "Bundle Discount": index === 0 ? `$${pricing.bundle_discount}` : "",
        "Bundle Price to Pay": index === 0 ? `$${pricing.bundle_price_to_pay}` : "",
      }))
    );

    if (exportData.length === 0) {
      alert("No valid data to export.");
      return;
    }

    secureExportToExcel(exportData, "marketing_skus.xlsx");
  };

  return (
    <div>
      <div
        className="content"
        style={{
          width: isRightSideBarVisible ? `calc(100% - ${sidebarWidth}px)` : "100%",
          transition: "width 0.3s ease-in-out",
        }}>
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Marketing SKUs</h4>
              <h6>Manage your marketing sku details</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <div className="search-set">
              <div className="search-input">
                <input
                  type="text"
                  placeholder="Search"
                  className="form-control form-control-sm formsearch"
                  value={searchQuery}
                  // onChange={handleSearch}
                />
                <Link to="#" className="btn btn-searchset">
                  <i data-feather="search" className="feather-search" />
                </Link>
              </div>
            </div>
            <li>
              <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                <Link data-bs-toggle="tooltip" data-bs-placement="top" onClick={exportToExcel}>
                  <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link data-bs-toggle="tooltip" data-bs-placement="top" onClick={fetchOfferPricings}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch(setToogleHeader(!data));
                  }}>
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>

          <div className="page-btn">
            <button
              className="btn btn-added"
              onClick={() => {
                if (!selectedClients) {
                  message.warning("Please select a client.");
                  return;
                }
                if (selectedClients.length === 0) {
                  message.warning("Please select a client.");
                  return;
                }

                if (selectedClients.length > 1) {
                  message.warning("Please select one client.");
                  return;
                }

                if (selectedClients[0] === "all") {
                  message.warning("Please select one client.");
                  return;
                }
                setClientId(selectedClients[0]);
                setCurrentMarketingSKUId(null);
                setIsRightSideBarVisible(true);
              }}>
              <PlusCircle className="me-2 iconsize" />
              Create Marketing SKU
            </button>
          </div>
        </div>
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <Table
                dataSource={processedData}
                columns={columns}
                pagination={false}
                loading={loading}
                footer={() => {
                  const displayedCount = searchQuery
                    ? filteredOfferPricings.length
                    : offerPricings.length;
                  const totalCount = searchQuery ? filteredOfferPricings.length : totalItems;

                  return (
                    <div className="info-footer">
                      <div className="data-info">
                        {displayedCount > 0 && (
                          <div>Showing all {displayedCount} marketing SKUs</div>
                        )}
                        {apiTiming.total_time && (
                          <div className="timing-info">
                            API response time: {Math.round(apiTiming.total_time * 1000)}ms
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <IndependentSKU
        isVisible={isRightSideBarVisible}
        onClose={() => {
          setIsRightSideBarVisible(!isRightSideBarVisible);
          setCurrentMarketingSKUId(null);
          fetchOfferPricings();
        }}
        skuId={currentMarketingSKUId}
        ref={sidebarRef}
        onResize={setSidebarWidth}
        clientId={clientId}
      />
    </div>
  );
};

export default OfferPricingList;
